import { useCallback, useState } from 'react';
import { z } from 'zod';

import { rage } from '~/rage';
import { Button } from '../../components/Button';
import {
	ButtonContainer,
	FormContainer,
	PanelContainer,
} from '../../components/Containers';
import { FormTitle } from '../../components/FormTitle';
import { LoginHints } from '../../components/LoginHints';
import { TextInput } from '../../components/TextInput';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import { AuthTabs, setActiveTab } from '../../redux/slices/authTabsSlice';
import {
	IFormError,
	setEmail,
	setLoading,
} from '../../redux/slices/formDataSlice';

const accountEmailSchema = z
	.string()
	.min(1, 'Почта обязательна для заполнения')
	.email('Некорректный формат почты');

const accountLoginSchema = z
	.string()
	.min(1, 'Логин обязателен для заполнения')
	.min(5, 'Логин должен содержать минимум 5 символов')
	.max(32, 'Логин не должен превышать 32 символа');
// .regex(
// 	/^[a-zA-Z0-9_-]+$/,
// 	'Разрешены латиница, цифры, дефис и подчёркивание',
// );

const accountPasswordSchema = z
	.string()
	.min(1, 'Пароль обязателен для заполнения')
	.min(6, 'Пароль должен содержать минимум 6 символов')
	.max(64, 'Пароль не должен превышать 64 символа');
// .regex(/[a-zA-Z]/, 'Пароль должен содержать буквы')
// .regex(/\d/, 'Пароль должен содержать цифры');
// .regex(/[!@#$%^&*_-]/, 'Пароль должен содержать хотя бы один спецсимвол');

const accountLoginOrEmailSchema = z
	.string()
	.min(1, 'Введите логин или почту')
	.superRefine((value, ctx) => {
		if (value.includes('@') && value.includes('.')) {
			const emailResult = accountEmailSchema.safeParse(value);
			if (!emailResult.success) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: emailResult.error.errors[0].message,
				});
			}
		} else {
			const loginResult = accountLoginSchema.safeParse(value);
			if (!loginResult.success) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: loginResult.error.errors[0].message,
				});
			}
		}
	});

const loginSchema = z.object({
	emailOrLogin: accountLoginOrEmailSchema,
	password: accountPasswordSchema,
});

const Login = () => {
	const [password, setPassword] = useState('');
	const [remember, setRemember] = useState(true);
	const [errors, setErrors] = useState<IFormError[]>([]);

	const emailOrLogin = useLayoutSelector((state) => state.formData.email);
	const loading = useLayoutSelector((state) => state.formData.loading);
	const dispatch = useLayoutDispatch();

	const handleSubmit = async () => {
		dispatch(setLoading(true));

		const errors: IFormError[] = [];
		try {
			await loginSchema.parseAsync({
				emailOrLogin,
				password,
			});
			await rage.accounts.login(emailOrLogin, password, remember);
		} catch (error) {
			if (error instanceof z.ZodError) {
				error.issues.forEach((err) => {
					if (err.path && typeof err.path[0] === 'string') {
						errors.push({ source: err.path[0], text: err.message });
					}
				});
			} else {
				rage.notifications.showException(error);
			}
		}

		setErrors(errors);
		dispatch(setLoading(false));
	};

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (errors !== null) {
				const { name, value } = event.target;
				if (name === 'email') {
					dispatch(setEmail(value));
				} else if (name === 'password') {
					setPassword(value);
				}
				setErrors(errors.filter((error) => error.source !== name));
			}
		},
		[errors],
	);

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.currentTarget;
		setRemember(checked);
	};

	const handleRegisterClick = () => {
		dispatch(setActiveTab(AuthTabs.Register));
	};

	return (
		<PanelContainer>
			<FormTitle
				text={
					'Добро пожаловать на CCDPlanet!\n' +
					'Войдите под своим аккаунтом или создайте новый!'
				}
			/>
			<FormContainer>
				<TextInput
					name="email"
					placeholder="Введите ваш логин / почту"
					error={errors.find((error) => error.source === 'email')?.text}
					defaultValue={emailOrLogin}
					onChange={handleInputChange}
					tabIndex={1}
				/>
				<TextInput
					name="password"
					placeholder="Введите пароль"
					error={errors.find((error) => error.source === 'password')?.text}
					onChange={handleInputChange}
					tabIndex={2}
				/>
				<LoginHints disabled={loading} onChange={handleCheckboxChange} />
			</FormContainer>
			<ButtonContainer>
				<Button
					primary
					name="login"
					type="button"
					loading={loading}
					onClick={handleSubmit}
					tabIndex={3}
				>
					Войти
				</Button>
				<Button
					name="register"
					disabled={loading}
					onClick={handleRegisterClick}
					tabIndex={4}
				>
					Регистрация
				</Button>
			</ButtonContainer>
		</PanelContainer>
	);
};

export { Login };
